import {Icon} from '@ezcater/tapas';
import {faSpinnerThird} from '@fortawesome/pro-solid-svg-icons';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import {FulfillmentDetailStrategy} from '@/graphql/types';

export type DeliveryPickupToggleVariant = 'primary' | 'light';

type DeliveryPickupToggleProps = {
  disabled?: boolean;
  idSuffix: string;
  isActive?: boolean;
  onChange: (value: FulfillmentDetailStrategy) => void;
  /** Shows a spinner on this option while a strategy change is in flight. */
  pendingValue?: FulfillmentDetailStrategy | null;
  value: FulfillmentDetailStrategy;
  variant?: DeliveryPickupToggleVariant;
};

const VARIANT_CLASSES: Record<DeliveryPickupToggleVariant, {selectedLabel: string; thumb: string}> =
  {
    primary: {selectedLabel: 'text-white', thumb: 'bg-green-700'},
    light: {selectedLabel: 'text-peppercorn-800', thumb: 'bg-white shadow-sm'},
  };

export const FULFILLMENT_OPTIONS = [
  FulfillmentDetailStrategy.Delivery,
  FulfillmentDetailStrategy.Takeout,
];

export const fulfillmentOptionId = (value: FulfillmentDetailStrategy, idSuffix: string) =>
  `where-fulfillment-${value}-${idSuffix}`;

export const fulfillmentRadiogroupId = (idSuffix: string) =>
  `where-fulfillment-radiogroup-${idSuffix}`;

const DeliveryPickupToggle: React.FC<DeliveryPickupToggleProps> = ({
  disabled = false,
  idSuffix,
  isActive = false,
  onChange,
  pendingValue = null,
  value,
  variant = 'primary',
}) => {
  const {t} = useTranslation('app-bar');
  const selectedIndex = FULFILLMENT_OPTIONS.indexOf(value);
  const variantClasses = VARIANT_CLASSES[variant];

  return (
    <div
      aria-label={t('eventBarV2.where.fulfillment.label')}
      className="relative flex rounded-full bg-peppercorn-50 p-1"
      data-testid="where-delivery-pickup-toggle"
      id={fulfillmentRadiogroupId(idSuffix)}
      role="radiogroup"
    >
      <span
        aria-hidden
        className={twMerge(
          'absolute inset-y-1 left-1 w-[calc(50%_-_0.25rem)] rounded-full transition-transform duration-200 ease-out motion-reduce:transition-none',
          variantClasses.thumb,
          selectedIndex === 1 && 'translate-x-full',
          isActive && 'ring-2 ring-peppercorn-800',
        )}
        data-testid="where-delivery-pickup-toggle-thumb"
      />
      {FULFILLMENT_OPTIONS.map(option => {
        const isSelected = option === value;
        const label = t(`eventBarV2.where.fulfillment.${option}`);
        return (
          <button
            aria-checked={isSelected}
            className={twMerge(
              'relative z-10 flex-1 cursor-pointer rounded-full px-6 py-2 text-center text-sm font-semibold transition-colors',
              isSelected ? variantClasses.selectedLabel : 'text-peppercorn-800',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            disabled={disabled}
            id={fulfillmentOptionId(option, idSuffix)}
            key={option}
            onClick={() => onChange(option)}
            onMouseDown={event => event.preventDefault()}
            role="radio"
            tabIndex={-1}
            type="button"
          >
            {option === pendingValue ? (
              <Icon
                aria-label={label}
                className="mx-auto block animate-spin"
                icon={faSpinnerThird}
                size="small"
              />
            ) : (
              label
            )}
          </button>
        );
      })}
    </div>
  );
};

export default DeliveryPickupToggle;
